/**
 * Institutional Blueprint & Template Library — Design Studio extension.
 *
 * Governance: Design Studio remains the authoritative configuration engine.
 * This page is a controlled provisioning accelerator only — every action
 * calls the Design Studio-scoped Blueprint API which itself reuses the
 * Design Studio configuration engine. Post-deployment, tenants become
 * fully editable through the Design Studio (§1, §2, §15).
 */
import React, { useEffect, useMemo, useState } from "react";
import client from "../lib/api";
import { PageHeader, Section, Loading, EmptyState } from "../components/Common";
import { toast } from "sonner";
import {
  BookMarked, Copy, ShieldCheck, Rocket, Eye, Loader2, Archive,
  CheckCircle2, Layers, FilePlus, GraduationCap,
} from "lucide-react";

const STATUS_COLOUR = {
  draft: "bg-slate-100 text-slate-600",
  under_review: "bg-amber-50 text-amber-700",
  approved: "bg-blue-50 text-blue-700",
  published: "bg-emerald-50 text-emerald-700",
  deprecated: "bg-slate-100 text-slate-500",
  archived: "bg-rose-50 text-rose-700",
};

export default function BlueprintLibrary() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [running, setRunning] = useState("");
  const [showDeploy, setShowDeploy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/blueprints");
      setItems(data.items || []);
      setCategories(data.categories || {});
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openBlueprint = async (id) => {
    setSelected(id);
    setCompliance(null);
    const { data } = await client.get(`/blueprints/${id}`);
    setDetail(data);
  };

  const runValidation = async () => {
    if (!selected) return;
    setRunning("validate");
    try {
      const { data } = await client.post(`/blueprints/${selected}/validate`);
      setCompliance(data);
      if (!data.deployable) toast.error(`${data.counts.fail} FAIL(s) — deployment blocked`);
      else toast.success(`${data.counts.pass} PASS · ${data.counts.warning} WARN — deployable`);
    } catch { toast.error("Validation failed"); }
    finally { setRunning(""); }
  };

  const cloneFromCurrentTenant = async () => {
    const name = prompt("Blueprint name?");
    if (!name) return;
    setRunning("clone");
    try {
      const { data } = await client.post("/blueprints/from-tenant", { name });
      toast.success(`Blueprint '${data.name}' created from your tenant`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clone failed");
    } finally { setRunning(""); }
  };

  const transition = async (target) => {
    if (!selected) return;
    setRunning("transition");
    try {
      await client.post(`/blueprints/${selected}/transition`, { status: target });
      await openBlueprint(selected);
      await load();
      toast.success(`Transitioned → ${target}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Transition failed"); }
    finally { setRunning(""); }
  };

  const badge = (s) => <span className={`text-[10px] uppercase tracking-widest rounded px-2 py-0.5 font-bold ${STATUS_COLOUR[s] || "bg-slate-100 text-slate-600"}`}>{s?.replace("_", " ")}</span>;

  return (
    <div data-testid="blueprint-library">
      <PageHeader
        kicker="Design Studio Extension"
        title="Institutional Blueprint & Template Library"
        subtitle="Reusable institutional configuration packages. Blueprints exist solely to accelerate onboarding — the Design Studio remains the authoritative configuration engine post-deployment."
        actions={
          <button onClick={cloneFromCurrentTenant} disabled={running === "clone"} data-testid="btn-clone-tenant"
            className="hl-btn-accent inline-flex items-center gap-2 text-sm px-4 py-2 disabled:opacity-60">
            {running === "clone" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
            Create blueprint from this tenant
          </button>
        }
      />

      {loading ? <Loading label="Loading blueprints…" /> : (
        <div className="grid lg:grid-cols-[380px_1fr] gap-6">
          <div className="hl-card overflow-hidden" data-testid="bp-list">
            <div className="p-3 border-b border-slate-100 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
              {items.length} blueprint{items.length === 1 ? "" : "s"}
            </div>
            <div className="max-h-[640px] overflow-y-auto">
              {items.length === 0 && <div className="p-6"><EmptyState title="No blueprints yet" description="Clone from your tenant or create a new blueprint to begin." /></div>}
              {items.map((b) => (
                <button key={b.id} onClick={() => openBlueprint(b.id)} data-testid={`bp-item-${b.slug}`}
                  className={`w-full text-left p-3 border-b border-slate-100 hover:bg-slate-50 transition ${selected === b.id ? "bg-slate-50 border-l-4 border-l-[color:var(--hl-orange)]" : ""}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-bold text-sm text-slate-800">{b.name}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{b.category} · {b.sub_category || "—"} · v{b.version}</div>
                    </div>
                    {badge(b.status)}
                  </div>
                  {b.description && <div className="text-xs text-slate-500 mt-1.5 line-clamp-2">{b.description}</div>}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            {!detail && <EmptyState title="Select a blueprint" description="Choose a blueprint to view its configuration, run compliance, or deploy a new institution." icon={<BookMarked className="w-8 h-8" />} />}
            {detail && (
              <>
                <div className="hl-card p-5" data-testid="bp-detail">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 font-bold">Blueprint</div>
                      <h2 className="font-display font-black text-2xl mt-1">{detail.name}</h2>
                      <div className="text-xs text-slate-500 mt-1">Version {detail.version} · {detail.category} · {detail.sub_category || "—"}</div>
                    </div>
                    <div className="text-right">{badge(detail.status)}</div>
                  </div>
                  {detail.description && <div className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{detail.description}</div>}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-xs">
                    <div><div className="text-slate-400 uppercase tracking-widest text-[10px]">Author</div><div className="font-mono">{detail.author_email || "—"}</div></div>
                    <div><div className="text-slate-400 uppercase tracking-widest text-[10px]">Approved by</div><div className="font-mono">{detail.approved_by || "—"}</div></div>
                    <div><div className="text-slate-400 uppercase tracking-widest text-[10px]">Deployed</div><div className="font-mono">{detail.deployment_count || 0} time(s)</div></div>
                    <div><div className="text-slate-400 uppercase tracking-widest text-[10px]">Last deployed</div><div className="font-mono">{(detail.last_deployed_at || "").slice(0, 10) || "—"}</div></div>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-5">
                    <button onClick={runValidation} disabled={running === "validate"} data-testid="btn-bp-validate"
                      className="hl-btn-outline inline-flex items-center gap-2 text-xs px-3 py-2 disabled:opacity-60">
                      {running === "validate" ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
                      Run compliance
                    </button>
                    {detail.status === "draft" && (
                      <button onClick={() => transition("under_review")} className="hl-btn-outline text-xs px-3 py-2 inline-flex items-center gap-1"><Eye className="w-3 h-3" /> Submit for review</button>
                    )}
                    {detail.status === "under_review" && (
                      <button onClick={() => transition("approved")} className="hl-btn-outline text-xs px-3 py-2 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Approve</button>
                    )}
                    {detail.status === "approved" && (
                      <button onClick={() => transition("published")} className="hl-btn-accent text-xs px-3 py-2 inline-flex items-center gap-1"><Layers className="w-3 h-3" /> Publish</button>
                    )}
                    {(detail.status === "published" || detail.status === "approved") && (
                      <button onClick={() => setShowDeploy(true)} data-testid="btn-bp-deploy" className="hl-btn-accent text-xs px-3 py-2 inline-flex items-center gap-1"><Rocket className="w-3 h-3" /> Deploy new institution</button>
                    )}
                    {detail.status !== "archived" && (
                      <button onClick={() => transition("archived")} className="hl-btn-outline text-xs px-3 py-2 inline-flex items-center gap-1 text-rose-700"><Archive className="w-3 h-3" /> Archive</button>
                    )}
                  </div>
                </div>

                {compliance && (
                  <div className="hl-card overflow-hidden" data-testid="bp-compliance">
                    <div className="p-3 border-b border-slate-100 flex items-center justify-between">
                      <div className="text-xs uppercase tracking-widest font-bold text-slate-700">Compliance report</div>
                      <div className="text-xs text-slate-500">
                        Overall <b>{compliance.overall}</b> · Deployable <b>{String(compliance.deployable)}</b> ·
                        {" "}<span className="text-emerald-700">{compliance.counts.pass} P</span> ·
                        {" "}<span className="text-amber-700">{compliance.counts.warning} W</span> ·
                        {" "}<span className="text-rose-700">{compliance.counts.fail} F</span>
                      </div>
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                      <table className="hl-table w-full text-xs">
                        <thead><tr><th>Check</th><th>Status</th><th>Message</th></tr></thead>
                        <tbody>
                          {compliance.checks.map((c) => (
                            <tr key={c.check}>
                              <td className="font-mono text-[11px]">{c.check}</td>
                              <td>
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${c.status === "PASS" ? "bg-emerald-50 text-emerald-700" : c.status === "WARNING" ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700"}`}>{c.status}</span>
                              </td>
                              <td className="text-slate-600">{c.message}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {showDeploy && detail && (
        <DeployModal blueprint={detail} onClose={() => setShowDeploy(false)} onDeployed={async (result) => {
          setShowDeploy(false);
          await openBlueprint(selected);
          await load();
          toast.success(`Tenant '${result.name}' deployed`);
        }} />
      )}
    </div>
  );
}

function DeployModal({ blueprint, onClose, onDeployed }) {
  const [form, setForm] = useState({
    tenant_name: "",
    tenant_slug: "",
    admin_email: "",
    admin_password: "",
    admin_first_name: "",
    admin_last_name: "",
    short_name: "",
    country: "Zimbabwe",
    city: "",
    motto: "",
    primary_color: (blueprint.config?.brand?.primary_color) || "#0A2E52",
    accent_color: (blueprint.config?.brand?.accent_color) || "#C9A227",
  });
  const [deploying, setDeploying] = useState(false);
  const auto = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const setF = (patch) => setForm((f) => {
    const next = { ...f, ...patch };
    if ("tenant_name" in patch && !f.tenant_slug) next.tenant_slug = auto(patch.tenant_name || "");
    return next;
  });

  const deploy = async () => {
    if (!form.tenant_name || !form.tenant_slug || !form.admin_email) {
      toast.error("Institution name, slug and admin email are required");
      return;
    }
    setDeploying(true);
    try {
      const { data } = await client.post(`/blueprints/${blueprint.id}/deploy`, {
        tenant_name: form.tenant_name,
        tenant_slug: form.tenant_slug,
        overrides: {
          admin_email: form.admin_email,
          admin_password: form.admin_password || undefined,
          admin_first_name: form.admin_first_name || undefined,
          admin_last_name: form.admin_last_name || undefined,
          short_name: form.short_name || undefined,
          country: form.country || undefined,
          city: form.city || undefined,
          motto: form.motto || undefined,
          primary_color: form.primary_color,
          accent_color: form.accent_color,
        },
      });
      onDeployed(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deployment failed");
    } finally { setDeploying(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="deploy-modal">
        <div className="p-5 border-b border-slate-100">
          <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 font-bold">Deployment Wizard</div>
          <h3 className="font-display font-black text-xl mt-1">Deploy from '{blueprint.name}'</h3>
          <div className="text-xs text-slate-500 mt-1">A new independent tenant will be provisioned. After deployment it becomes fully editable via the Design Studio.</div>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Institution name *"><input data-testid="deploy-name" value={form.tenant_name} onChange={(e) => setF({ tenant_name: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" /></Field>
            <Field label="Slug (URL)"><input data-testid="deploy-slug" value={form.tenant_slug} onChange={(e) => setF({ tenant_slug: e.target.value })} className="w-full px-3 py-2 border rounded text-sm font-mono" /></Field>
            <Field label="Short name"><input value={form.short_name} onChange={(e) => setF({ short_name: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" /></Field>
            <Field label="Country"><input value={form.country} onChange={(e) => setF({ country: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" /></Field>
            <Field label="City"><input value={form.city} onChange={(e) => setF({ city: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" /></Field>
            <Field label="Motto"><input value={form.motto} onChange={(e) => setF({ motto: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" /></Field>
            <Field label="Primary colour"><input type="color" value={form.primary_color} onChange={(e) => setF({ primary_color: e.target.value })} className="w-full h-10 border rounded" /></Field>
            <Field label="Accent colour"><input type="color" value={form.accent_color} onChange={(e) => setF({ accent_color: e.target.value })} className="w-full h-10 border rounded" /></Field>
          </div>
          <div className="pt-3 border-t border-slate-100 mt-2">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Primary Administrator</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Email *"><input data-testid="deploy-admin-email" value={form.admin_email} onChange={(e) => setF({ admin_email: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" /></Field>
              <Field label="Temporary password (auto-generated if blank)"><input value={form.admin_password} onChange={(e) => setF({ admin_password: e.target.value })} className="w-full px-3 py-2 border rounded text-sm font-mono" /></Field>
              <Field label="First name"><input value={form.admin_first_name} onChange={(e) => setF({ admin_first_name: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" /></Field>
              <Field label="Last name"><input value={form.admin_last_name} onChange={(e) => setF({ admin_last_name: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" /></Field>
            </div>
          </div>
        </div>
        <div className="p-5 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="hl-btn-outline text-sm px-4 py-2">Cancel</button>
          <button onClick={deploy} disabled={deploying} data-testid="btn-confirm-deploy" className="hl-btn-accent text-sm px-4 py-2 inline-flex items-center gap-2 disabled:opacity-60">
            {deploying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
            {deploying ? "Provisioning…" : "Deploy tenant"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">{label}</div>{children}</label>;
}
